import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { toast } from 'react-toastify';
import { getImageUrl } from '../../services/api';
import PageTitle from '../shared/PageTitle';
import './SettingsPage.css';

// Create a reusable SectionHeader component at the top of the file
const SectionHeader = ({ children, settings }) => (
  <div 
    className="settings-section-header" 
    style={{
      backgroundColor: settings.block_title_bg_color || '#8B4513', 
      color: settings.block_title_color || 'white',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '15px',
      fontWeight: 'bold'
    }}
  >
    {children}
  </div>
);

// Define default values to prevent uncontrolled to controlled warnings
const getDefaultFormSettings = (settings = {}) => {
  // First spread all settings, then override specific ones that need conversion
  const baseSettings = {
    society_name: settings.society_name || '',
    society_address: settings.society_address || '',
    contact_email: settings.contact_email || '',
    contact_phone: settings.contact_phone || '',
    logo_url: settings.logo_url || '',
    logo_width: settings.logo_width || '80',
    dwelling_unit_count: settings.dwelling_unit_count || '',
    maintenance_fee: settings.maintenance_fee || '',
    additional_charges: settings.additional_charges || '',
    theme: settings.theme || 'light',
    theme_primary_color: settings.theme_primary_color || '#D67836',
    theme_secondary_color: settings.theme_secondary_color || '#6c757d',
    theme_accent_color: settings.theme_accent_color || '#ffc107',
    sidebar_bg_color: settings.sidebar_bg_color || '#ffffff',
    sidebar_text_color: settings.sidebar_text_color || '#212529',
    sidebar_active_bg_color: settings.sidebar_active_bg_color || '#D67836',
    sidebar_active_text_color: settings.sidebar_active_text_color || '#ffffff',
    sidebar_hover_bg_color: settings.sidebar_hover_bg_color || '#f8f9fa',
    sidebar_hover_text_color: settings.sidebar_hover_text_color || '#D67836',
    sidebar_border_color: settings.sidebar_border_color || '#dee2e6',
    sidebar_width: settings.sidebar_width || '250',
    sidebar_icon_color: settings.sidebar_icon_color || '#212529',
    block_title_bg_color: settings.block_title_bg_color || '#f8f9fa',
    block_title_color: settings.block_title_color || '#8B4513',
    h1_color: settings.h1_color || '#212529',
    h2_color: settings.h2_color || '#212529',
    h3_color: settings.h3_color || '#212529',
    text_primary_color: settings.text_primary_color || '#333333',
    text_secondary_color: settings.text_secondary_color || '#6c757d',
    custom_css: settings.custom_css || '',
    currency: settings.currency || 'INR',
    currencySymbol: settings.currencySymbol || '₹',
    dateFormat: settings.dateFormat || 'DD/MM/YYYY',
    financial_year: settings.financial_year || '',
    tabs_bg_color: settings.tabs_bg_color || '#fafafa',
    tabs_text_color: settings.tabs_text_color || '#212529',
    tabs_active_bg_color: settings.tabs_active_bg_color || '#0d6efd',
    tabs_active_text_color: settings.tabs_active_text_color || '#ffffff',
    tabs_hover_bg_color: settings.tabs_hover_bg_color || '#f8f9fa',
    tabs_hover_text_color: settings.tabs_hover_text_color || '#0d6efd',
    // Button color settings
    btn_primary_bg: settings.btn_primary_bg || settings.theme_primary_color || '#0d6efd',
    btn_primary_text: settings.btn_primary_text || '#ffffff',
    btn_primary_hover_bg: settings.btn_primary_hover_bg || '#0b5ed7',
    btn_secondary_bg: settings.btn_secondary_bg || '#6c757d',
    btn_secondary_text: settings.btn_secondary_text || '#ffffff',
    btn_secondary_hover_bg: settings.btn_secondary_hover_bg || '#5a6268',
    btn_success_bg: settings.btn_success_bg || '#198754',
    btn_success_text: settings.btn_success_text || '#ffffff',
    btn_success_hover_bg: settings.btn_success_hover_bg || '#157347',
    btn_danger_bg: settings.btn_danger_bg || '#dc3545',
    btn_danger_text: settings.btn_danger_text || '#ffffff',
    btn_danger_hover_bg: settings.btn_danger_hover_bg || '#bb2d3b',
    btn_warning_bg: settings.btn_warning_bg || '#ffc107',
    btn_warning_text: settings.btn_warning_text || '#000000',
    btn_warning_hover_bg: settings.btn_warning_hover_bg || '#ffca2c',
    btn_info_bg: settings.btn_info_bg || '#0dcaf0',
    btn_info_text: settings.btn_info_text || '#000000',
    btn_info_hover_bg: settings.btn_info_hover_bg || '#31d2f2',
    btn_outline_border: settings.btn_outline_border || settings.theme_primary_color || '#0d6efd',
    btn_outline_text: settings.btn_outline_text || settings.theme_primary_color || '#0d6efd',
    btn_outline_hover_bg: settings.btn_outline_hover_bg || settings.theme_primary_color || '#0d6efd',
    btn_outline_hover_text: settings.btn_outline_hover_text || '#ffffff',
    btn_disabled_bg: settings.btn_disabled_bg || '#e9ecef',
    btn_disabled_text: settings.btn_disabled_text || '#6c757d',
    btn_disabled_border: settings.btn_disabled_border || '#e9ecef',
    show_owner_contact: settings.show_owner_contact ?? false,
    scanner_owner_name: settings.scanner_owner_name || '',
    scanner_upi_id: settings.scanner_upi_id || '',
    scanner_upi_number: settings.scanner_upi_number || '',
    google_pay_upi: settings.google_pay_upi || '',
    google_pay_payee_name: settings.google_pay_payee_name || '',
    paytm_upi: settings.paytm_upi || '',
    paytm_payee_name: settings.paytm_payee_name || '',
    phonepe_upi: settings.phonepe_upi || '',
    phonepe_payee_name: settings.phonepe_payee_name || '',
    scanner_qr_url: settings.scanner_qr_url || '',
    show_society_transactions: settings.show_society_transactions ?? false,
    // Spread all other settings
    ...settings
  };
  
  // Now explicitly handle boolean conversions that must not be overridden
  const processedSettings = {
    ...baseSettings,
    // Explicitly convert show_society_name to boolean - this must come after the spread
    show_society_name: settings.show_society_name === 'true' || settings.show_society_name === true
  };
  
  // Debug logging for show_society_name
  console.log('getDefaultFormSettings - Input show_society_name:', settings.show_society_name, 'Type:', typeof settings.show_society_name);
  console.log('getDefaultFormSettings - Output show_society_name:', processedSettings.show_society_name, 'Type:', typeof processedSettings.show_society_name);
  
  return processedSettings;
};

const Settings = () => {
  const { settings, saveSettings, uploadLogo, uploadScanner ,forceReloadSettings } = useSettings();
  const [saving, setSaving] = useState(false);
  const [formSettings, setFormSettings] = useState(() => getDefaultFormSettings(settings));
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('society');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const settingsAppliedRef = useRef(false);
  // Memoize logo URL to prevent changing timestamps on each render
  const memoizedLogoUrl = useMemo(() => {
    if (!formSettings.logo_url) {
      console.log('memoizedLogoUrl - No logo_url in formSettings');
      return '';
    }
    console.log('memoizedLogoUrl - formSettings.logo_url:', formSettings.logo_url);
    const imageUrl = getImageUrl(formSettings.logo_url);
    console.log('memoizedLogoUrl - getImageUrl result:', imageUrl);
    return imageUrl;
  }, [formSettings.logo_url]);

  const [scannerFile, setScannerFile] = useState(null);
  const [scannerPreview, setScannerPreview] = useState('');
  // Memoize logo URL to prevent changing timestamps on each render
  const memoizedScannerUrl = useMemo(() => {
    if (!formSettings.scanner_qr_url) {
      console.log('memoizedScannerUrl - No scanner_qr_url in formSettings');
      return '';
    }
    console.log('memoizedScannerUrl - formSettings.scanner_qr_url:', formSettings.scanner_qr_url);
    const imageUrl = getImageUrl(formSettings.scanner_qr_url);
    console.log('memoizedScannerUrl - getImageUrl result:', imageUrl);
    return imageUrl;
  }, [formSettings.scanner_qr_url]);
  
  // Function to update sidebar styles - now uses settings directly instead of localStorage
  const updateSidebarStyles = useCallback((settingsToApply) => {
    const sidebarBgColor = settingsToApply.sidebar_bg_color || '#ffffff';
    const sidebarTextColor = settingsToApply.sidebar_text_color || '#212529';
    const sidebarIconColor = settingsToApply.sidebar_icon_color || '#212529';
    const sidebarActiveBgColor = settingsToApply.sidebar_active_bg_color || '#0d6efd';
    const sidebarActiveTextColor = settingsToApply.sidebar_active_text_color || '#ffffff';
    const sidebarHoverBgColor = settingsToApply.sidebar_hover_bg_color || '#f8f9fa';
    const sidebarHoverTextColor = settingsToApply.sidebar_hover_text_color || '#0d6efd';
    const sidebarBorderColor = settingsToApply.sidebar_border_color || '#dee2e6';
    const sidebarWidth = settingsToApply.sidebar_width || '250';
    
    let overrideStyle = document.getElementById('sidebar-navigation-override');
    if (!overrideStyle) {
      overrideStyle = document.createElement('style');
      overrideStyle.id = 'sidebar-navigation-override';
      document.head.appendChild(overrideStyle);
    }
    
    overrideStyle.textContent = `
      .sidebar-navigation { background-color: ${sidebarBgColor} !important; width: ${sidebarWidth}px !important; border-right: 1px solid ${sidebarBorderColor} !important; }
      .sidebar-navigation .menu-icon, .sidebar-navigation .fas, .sidebar-navigation .far { color: ${sidebarIconColor} !important; }
      .sidebar-navigation .menu-link, .sidebar-navigation .menu-text { color: ${sidebarTextColor} !important; }
      .sidebar-navigation .menu-item.active, .sidebar-navigation li.active, .sidebar-navigation .active { background-color: ${sidebarActiveBgColor} !important; }
      .sidebar-navigation .menu-item.active *, .sidebar-navigation li.active *, .sidebar-navigation .active * { color: ${sidebarActiveTextColor} !important; }
      .sidebar-navigation .menu-item:hover:not(.active), .sidebar-navigation .menu-link:hover:not(.active) { background-color: ${sidebarHoverBgColor} !important; }
      .sidebar-navigation .menu-item:hover:not(.active) *, .sidebar-navigation .menu-link:hover:not(.active) * { color: ${sidebarHoverTextColor} !important; }
    `;
  }, []);
  
  // Function to apply settings to the DOM
  const applySettings = useCallback((settingsToApply) => {
    if (!settingsToApply) {
      console.error("No settings provided to apply");
      return;
    }
    
    // Skip on certain paths
    if (typeof window !== 'undefined' && (
      window.location.pathname.startsWith('/superadmin') || 
      window.location.pathname === '/privacy-policy' ||
      window.location.pathname === '/support' ||
      window.location.pathname === '/forgot-password' ||
      window.location.pathname === '/reset-password' ||
      window.location.pathname === '/landing' ||
      window.location.pathname === '/contact'
    )) {
      console.log('Settings.js: Skipping CSS injection for path:', window.location.pathname);
      return;
    }
    
    console.log('Settings.js: Applying CSS for path:', window.location.pathname);
    
    try {
      // Apply theme
      document.documentElement.setAttribute('data-bs-theme', settingsToApply.theme || 'light');
      
      // Apply logo width - COMMENTED OUT TO PREVENT INLINE STYLES
      // if (settingsToApply.logo_width) {
      //   document.documentElement.style.setProperty('--logo-width', `${settingsToApply.logo_width}px`);
      // }
      
      // Apply color variables - COMMENTED OUT TO PREVENT INLINE STYLES
      // document.documentElement.style.setProperty('--bs-primary', settingsToApply.theme_primary_color || '#0d6efd');
      // document.documentElement.style.setProperty('--bs-secondary', settingsToApply.theme_secondary_color || '#6c757d');
      // document.documentElement.style.setProperty('--bs-accent', settingsToApply.theme_accent_color || '#ffc107');
      // document.documentElement.style.setProperty('--bs-block-title', settingsToApply.block_title_color || '#8B4513');
      // document.documentElement.style.setProperty('--bs-block-title-bg', settingsToApply.block_title_bg_color || '#f8f9fa');
      
      // Set theme variables for PageTitle.css and other components
      const primaryColor = settingsToApply.theme_primary_color || '#0d6efd';
      // document.documentElement.style.setProperty('--theme-primary', primaryColor);
      // document.documentElement.style.setProperty('--theme-primary-color', primaryColor);
      
      // Convert hex color to RGB for --theme-primary-rgb variable
      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        } : { r: 13, g: 110, b: 253 }; // Default blue fallback
      };
      
      const rgbColor = hexToRgb(primaryColor);
      // document.documentElement.style.setProperty('--theme-primary-rgb', `${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}`);
      
      // Debug logging to verify theme variables are set
      console.log('Theme variables set:', {
        '--theme-primary': primaryColor,
        '--theme-primary-color': primaryColor,
        '--theme-primary-rgb': `${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b}`
      });
      
      // Ensure block titles reflect the custom color settings - COMMENTED OUT TO PREVENT INLINE STYLES
      // document.documentElement.style.setProperty('--block-title-color', settingsToApply.block_title_color || '#8B4513');
      // document.documentElement.style.setProperty('--block-title-bg-color', settingsToApply.block_title_bg_color || '#f8f9fa');
      
      // Set tab variables for consistent theming - COMMENTED OUT TO PREVENT INLINE STYLES
      // document.documentElement.style.setProperty('--tabs-bg-color', settingsToApply.tabs_bg_color || '#fafafa');
      // document.documentElement.style.setProperty('--tabs-text-color', settingsToApply.tabs_text_color || '#212529');
      // document.documentElement.style.setProperty('--tabs-active-bg-color', settingsToApply.tabs_active_bg_color || primaryColor);
      // document.documentElement.style.setProperty('--tabs-active-text-color', settingsToApply.tabs_active_text_color || '#ffffff');
      // document.documentElement.style.setProperty('--tabs-hover-bg-color', settingsToApply.tabs_hover_bg_color || '#f8f9fa');
      // document.documentElement.style.setProperty('--tabs-hover-text-color', settingsToApply.tabs_hover_text_color || primaryColor);
      
      // Custom CSS
      const customCss = settingsToApply.custom_css || '';
      let customStyleElement = document.getElementById('app-custom-css-override');
      if (!customStyleElement) {
        customStyleElement = document.createElement('style');
        customStyleElement.id = 'app-custom-css-override';
        document.head.appendChild(customStyleElement);
      }
      customStyleElement.textContent = customCss;
      
      // Apply sidebar styles using direct settings
      updateSidebarStyles(settingsToApply);
      
      // Add high-specificity CSS for Settings tabs to ensure they use theme colors
      let settingsTabStyle = document.getElementById('settings-tabs-override');
      if (!settingsTabStyle) {
        settingsTabStyle = document.createElement('style');
        settingsTabStyle.id = 'settings-tabs-override';
        document.head.appendChild(settingsTabStyle);
      }
      
      settingsTabStyle.textContent = `
        /* High-specificity styles for Settings page tabs */
        .settings-tabs {
          border-bottom: 2px solid ${primaryColor} !important;
        }
        
        .settings-tabs .nav-link {
          color: ${settingsToApply.tabs_text_color || '#212529'} !important;
          background-color: ${settingsToApply.tabs_bg_color || 'transparent'} !important;
        }
        
        .settings-tabs .nav-link:hover:not(.active) {
          background-color: ${settingsToApply.tabs_hover_bg_color || 'rgba(13, 110, 253, 0.1)'} !important;
          color: ${settingsToApply.tabs_hover_text_color || primaryColor} !important;
        }
        
        .settings-tabs .nav-link.active {
          color: ${settingsToApply.tabs_active_text_color || '#ffffff'} !important;
          background-color: ${settingsToApply.tabs_active_bg_color || primaryColor} !important;
          border-color: ${settingsToApply.tabs_active_bg_color || primaryColor} !important;
        }
      `;

    } catch (error) {
      console.error("Error in applySettings:", error);
    }
  }, [updateSidebarStyles]);
  
  // Cleanup effect for style elements
  useEffect(() => {
    return () => {
      // Clean up the settings tabs style element on unmount
      const settingsTabStyle = document.getElementById('settings-tabs-override');
      if (settingsTabStyle) {
        settingsTabStyle.remove();
      }
    };
  }, []);
   
  
  // Add debug logging for settings changes
  useEffect(() => {
    // console.log('Current settings from context:', settings);
    if (!settings || Object.keys(settings).length === 0) {
      // console.log("No settings available yet from context");
      return;
    }
    
    // Log specific society settings
    // console.log('Society name from settings:', settings.society_name);
  }, [settings]);
  
  // Make sure formSettings is synchronized with actual settings
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only update if settings is populated and different from current formSettings
    if (settings && Object.keys(settings).length > 0) {
      console.log('useEffect - Settings from context - show_society_name:', settings.show_society_name, 'Type:', typeof settings.show_society_name);
      
      const shouldUpdate = Object.keys(settings).some(key => 
        settings[key] !== formSettings[key] && settings[key] !== undefined
      );
      
      if (shouldUpdate && !saving) {
        console.log('useEffect - Updating formSettings because settings changed');
        console.log('useEffect - Current formSettings show_society_name:', formSettings.show_society_name);
        const newFormSettings = getDefaultFormSettings(settings);
        console.log('useEffect - New formSettings show_society_name:', newFormSettings.show_society_name);
        setFormSettings(newFormSettings);
      }
    }
  }, [settings, saving]); // Removed formSettings from deps to prevent infinite loop
  
  // Apply settings when the settings state changes
  useEffect(() => {
    // console.log("Settings from context:", settings);
    if (!settings || Object.keys(settings).length === 0) {
      // console.log("Settings not available yet from context");
      return;
    }
    
    // Apply settings with a slight delay to ensure DOM is ready
    const timer = setTimeout(() => {
      try {
        // console.log("Applying settings from context");
        applySettings(settings);
        setFormSettings(getDefaultFormSettings(settings));
        settingsAppliedRef.current = true;
        // console.log("Settings applied from context");
      } catch (error) {
        console.error("Error applying settings from context:", error);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [settings, applySettings]);
  
  const currencies = [
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' }
  ];
  
  const dateFormats = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' }
  ];
  
  const themes = [
    { value: 'light', label: 'Light Theme' },
    { value: 'dark', label: 'Dark Theme' }
  ];
  
  const handleSettingChange = (event) => {
    const { name, value, type, checked } = event.target;
    const settingValue = type === 'checkbox' ? checked : value;

    let finalValue = settingValue;

    if (name === 'contact_phone') {
      const allowedCodes = {
        '91': 10,
        '971': 9,
        '1': 10,
      };

      // Remove all non-digit and plus
      let raw = finalValue.replace(/[^\d+]/g, '');

      // Allow full clearing (empty field)
      if (raw === '') {
        finalValue = '';
      } else {
        // Ensure '+' is only at the beginning
        if (raw.includes('+')) {
          raw = '+' + raw.replace(/\+/g, '');
        }

        // Prevent input if '+' is missing at the start
        if (!raw.startsWith('+')) {
          return;
        }

        // Try matching an allowed country code
        let matchedCode = null;
        for (let code of Object.keys(allowedCodes)) {
          if (raw.startsWith('+' + code)) {
            matchedCode = code;
            break;
          }
        }

        if (matchedCode) {
          const maxDigits = allowedCodes[matchedCode];
          const afterCode = raw.slice(matchedCode.length + 1);
          const digits = afterCode.replace(/\D/g, '').slice(0, maxDigits);
          finalValue = `+${matchedCode}${digits ? ' ' + digits : ''}`;
        } else {
          // No valid country code matched, allow user to type it
          finalValue = raw.slice(0, 4); // let user enter up to 3-digit code
        }
      }
    }

    const updatedSettings = {
      ...formSettings,
      [name]: finalValue,
    };

    setFormSettings(updatedSettings);

    if (['society_name', 'logo_url', 'show_society_name'].includes(name)) {
      try {
        localStorage.setItem(`app_${name}`, finalValue);
      } catch (e) {
        console.warn('Could not store setting in localStorage:', e);
      }
    }
  };
  
  const handleCurrencyChange = (e) => {
    const selectedCurrency = currencies.find(curr => curr.code === e.target.value);
    
    if (selectedCurrency) {
      setFormSettings({
        ...formSettings,
        currency: selectedCurrency.code,
        currencySymbol: selectedCurrency.symbol
      });
    }
  };
  
  const handleDateFormatChange = (e) => {
    setFormSettings({
      ...formSettings,
      dateFormat: e.target.value
    });
  };
  
  const handleThemeChange = (e) => {
    setFormSettings({
      ...formSettings,
      theme: e.target.value
    });
  };
  
  const handleColorChange = (e) => {
    const { name, value } = e.target;
    setFormSettings({
      ...formSettings,
      [name]: value
    });
  };
  
  // Handle logo file change
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Store the file for upload
      setLogoFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      toast.success('Logo file selected. Click "Save All Settings" to upload.');
    }
  };

   // Handle logo file change
  const handleScannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Validate file size (2MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        e.target.value = ''; // Clear the input
        return;
      }
      
      // Store the file for upload
      setScannerFile(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setScannerPreview(e.target.result);
      };
      reader.readAsDataURL(file);
      
      toast.success('Scanner file selected. Click "Save All Settings" to upload.');
    }
  };
  
  // Handle removing logo
  const handleRemoveLogo = async () => {
    try {
      const result = await uploadLogo(null, true); // true indicates remove
      if (result.success) {
        toast.success('Logo removed successfully!');
        // Update settings to reflect removal
        setFormSettings({
          ...formSettings,
          logo_url: ''
        });
        setLogoFile(null);
        setLogoPreview('');
      } else {
        toast.error('Failed to remove logo');
      }
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Error removing logo');
    }
  };

  // Handle removing logo
  const handleRemoveScanner = async () => {
    try {
      const result = await uploadScanner(null, true); // true indicates remove
      if (result.success) {
        toast.success('Scannner removed successfully!');
        // Update settings to reflect removal
        setFormSettings({
          ...formSettings,
          scanner_qr_url: ''
        });
        setScannerFile(null);
        setScannerPreview('');
      } else {
        toast.error('Failed to remove Scanner');
      }
    } catch (error) {
      toast.error('Error removing logo');
    }
  };
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Find the handleSave function and enhance it
  const handleSave = async () => {
    setSaving(true);
    
    // console.log('Saving settings:', formSettings);
    
    try {
      // Convert any null or undefined values to empty strings
      // Filter out any irrelevant or transient settings 
      const cleanSettings = {};
      Object.keys(formSettings).forEach(key => {
        // Skip keys that start with _ or are function references or file objects or are already empty
        if (!key.startsWith('_') && 
            typeof formSettings[key] !== 'function' && 
            !(formSettings[key] instanceof File) &&
            formSettings[key] !== null && 
            formSettings[key] !== undefined) {
          // Stringify objects
          if (typeof formSettings[key] === 'object') {
            cleanSettings[key] = JSON.stringify(formSettings[key]);
          } else {
            cleanSettings[key] = formSettings[key];
          }
        }
      });
      
      // console.log('Clean settings to save:', cleanSettings);
      
      // Save logo file if there is one
      if (logoFile) {
        console.log('handleSave - Uploading logo file:', logoFile.name, logoFile.size, logoFile.type);
        try {
          const logoResult = await uploadLogo(logoFile);
          console.log('handleSave - Logo upload result:', logoResult);
          if (logoResult.success) {
            cleanSettings.logo_url = logoResult.logo_url;
            console.log('handleSave - Logo URL set to:', logoResult.logo_url);
            toast.success('Logo uploaded successfully');
            // Clear the preview since it's now saved
            setLogoPreview('');
          } else {
            toast.error(`Failed to upload logo: ${logoResult.error || 'Unknown error'}`);
            console.error('Logo upload failed:', logoResult);
          }
        } catch (logoError) {
          console.error('Error uploading logo:', logoError);
          toast.error(`Error uploading logo: ${logoError.message}`);
        }
        
        // Clear the logo file state
        setLogoFile(null);
      }

      // Save logo file if there is one
      if (scannerFile) {
        console.log('handleSave - Uploading logo file:', scannerFile.name, scannerFile.size, scannerFile.type);
        try {
          const scannerResult = await uploadScanner(scannerFile);
          if (scannerResult.success) {
            cleanSettings.scanner_qr_url = scannerResult.scanner_qr_url;
            toast.success('Scanner uploaded successfully');
            setScannerPreview('');
          } else {
            toast.error(`Failed to upload logo: ${scannerResult.error || 'Unknown error'}`);
          }
        } catch (scannerError) {
          toast.error(`Error uploading logo: ${scannerError.message}`);
        }
        setScannerFile(null);
      }
      
      // Before saving via API, convert boolean show_society_name back to string
      if (Object.prototype.hasOwnProperty.call(cleanSettings, 'show_society_name')) {
        console.log('handleSave - Before conversion show_society_name:', cleanSettings.show_society_name, 'Type:', typeof cleanSettings.show_society_name);
        cleanSettings.show_society_name = cleanSettings.show_society_name ? 'true' : 'false';
        console.log('handleSave - After conversion show_society_name:', cleanSettings.show_society_name, 'Type:', typeof cleanSettings.show_society_name);
      }
      
      // Submit settings to server
      console.log('Submitting settings to API - show_society_name:', cleanSettings.show_society_name);
      
      const result = await saveSettings(cleanSettings);
      
      if (result.success) {
        toast.success('Settings saved successfully');
        
        // Force reload settings from server to ensure consistency
        await forceReloadSettings();
        
        // Update form settings to include the newly saved values
        setFormSettings(prevSettings => ({
          ...prevSettings,
          ...cleanSettings
        }));
        
        // Re-apply theme and styles, merging context settings with any newly saved values
        applySettings({ ...settings, ...cleanSettings });
      } else {
        toast.error('Error saving settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(`Error saving settings: ${error.message || 'An unexpected error occurred.'}`);
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="container-fluid mt-4 activity-container">
      <PageTitle 
        icon="fa-cogs" 
        title="Global Settings"
        action={
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-check me-2"></i>
                Save All Settings
              </>
            )}
          </button>
        }
      />
      
      {/* Main Settings Content with Horizontal Tabs */}
      <div className="settings-content-container">
        {/* Horizontal Tabs Navigation */}
        <ul className="nav nav-tabs settings-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'society' ? 'active' : ''}`}
              onClick={() => handleTabChange('society')}
            >
              <i className="fas fa-building me-2"></i> Society
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'dwelling' ? 'active' : ''}`}
              onClick={() => handleTabChange('dwelling')}
            >
              <i className="fas fa-home me-2"></i> Units
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'appearance' ? 'active' : ''}`}
              onClick={() => handleTabChange('appearance')}
            >
              <i className="fas fa-palette me-2"></i> Appearance
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'regional' ? 'active' : ''}`}
              onClick={() => handleTabChange('regional')}
            >
              <i className="fas fa-globe me-2"></i> Regional
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'system' ? 'active' : ''}`}
              onClick={() => handleTabChange('system')}
            >
              <i className="fas fa-cogs me-2"></i> System
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'advanced' ? 'active' : ''}`}
              onClick={() => handleTabChange('advanced')}
            >
              <i className="fas fa-code me-2"></i> Advanced
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'financial' ? 'active' : ''}`}
              onClick={() => handleTabChange('financial')}
            >
              <i className="fas fa-chart-line me-2"></i> Financial
            </button>
          </li>
        </ul>
        
        {/* Settings Content */}
        <div className="settings-content-area">
          {/* Society Information Tab */}
          {activeTab === 'society' && (
            <>
              <SectionHeader settings={settings}>Society Information</SectionHeader>
              <div className="settings-form-container">
                <div className="row mb-2">
                  <div className="col-md-6">
                      <label htmlFor="society_name" className="form-label">Society Name</label>
                      <input
                        type="text"
                        className="form-control"
                        id="society_name"
                        name="society_name"
                        value={formSettings.society_name}
                        onChange={handleSettingChange}
                        placeholder="Enter society name"
                      />
                      <small className="text-muted">The name of your society as it will appear in the app.</small>
                  </div>
                  <div className="col-md-6">
                      <label htmlFor="society_address" className="form-label">Society Address</label>
                      <input
                        type="text"
                        className="form-control"
                        id="society_address"
                        name="society_address"
                        value={formSettings.society_address}
                        onChange={handleSettingChange}
                        placeholder="Enter society address"
                      />
                  </div>
                </div>
                
                <div className="row mb-2">
                  <div className="col-md-6">
                      <label htmlFor="contact_email" className="form-label">Contact Email</label>
                      <input
                        type="email"
                        className="form-control"
                        id="contact_email"
                        name="contact_email"
                        value={formSettings.contact_email}
                        onChange={handleSettingChange}
                        placeholder="info@society.com"
                      />
                  </div>
                  <div className="col-md-6">
                      <label htmlFor="contact_phone" className="form-label">Contact Phone</label>
                      <input
                        type="text"
                        className="form-control"
                        id="contact_phone"
                        name="contact_phone"
                        value={formSettings.contact_phone}
                        onChange={handleSettingChange}
                        placeholder="+91 9876543210"
                      />
                  </div>
                </div>
                
                <div className="mb-2">
                  <label htmlFor="logo_url" className="form-label">Society Logo</label>
                  <div className="row align-items-start">
                    <div className="col-md-5">
                      <input 
                        type="file" 
                        className="form-control" 
                        id="logo_file" 
                        ref={fileInputRef}
                        onChange={handleLogoChange}
                        accept="image/*"
                      />
                      <small className="text-muted">Recommended size: 200x200 pixels. Max file size: 2MB.</small>
                    </div>
                    <div className="col-md-2">
                      {(logoPreview || memoizedLogoUrl) && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={handleRemoveLogo}
                          title="Remove current logo"
                        >
                          <i className="fas fa-trash logoRemoveTrash"></i>
                        </button>
                      )}
                    </div>
                    <div className="col-md-5 text-center">
                      {(logoPreview || memoizedLogoUrl) && (
                        <img 
                          src={logoPreview || memoizedLogoUrl}
                          alt="Society Logo Preview"
                          className="mt-2 img-thumbnail"
                          style={{ maxWidth: '100px', maxHeight: '100px' }}
                        />
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="mb-2">
                  <label htmlFor="logo_width" className="form-label">Logo Width in Navbar (px)</label>
                  <input
                    type="number"
                    className="form-control"
                    id="logo_width"
                    name="logo_width"
                    value={formSettings.logo_width}
                    onChange={handleSettingChange}
                    min="20"
                    max="200"
                  />
                </div>
                
                <div className="mb-2 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="show_society_name"
                    name="show_society_name"
                    checked={!!formSettings.show_society_name}
                    onChange={handleSettingChange}
                  />
                  <label className="form-check-label" htmlFor="show_society_name">
                    Show society name in header
                  </label>
                  <small className="form-text text-muted d-block">
                    When enabled, shows the organization name instead of the logo in the sidebar.
                  </small>
                </div>
              </div>
            </>
          )}
          
          {/* Dwelling Units Tab */}
          {activeTab === 'dwelling' && (
            <>
              <SectionHeader settings={settings}>Dwelling Units</SectionHeader>
              <div className="settings-form-container">
                <div className="row mb-3">
                  <div className="col-md-6">
                      <label htmlFor="dwelling_unit_count" className="form-label">Total Number of Units</label>
                      <input
                        type="number"
                        className="form-control"
                        id="dwelling_unit_count"
                        name="dwelling_unit_count"
                        value={formSettings.dwelling_unit_count}
                        onChange={handleSettingChange}
                        min="1"
                      />
                      <small className="text-muted">Maximum number of units that can be added to the system.</small>
                  </div>
                  <div className="col-md-6">
                      <label htmlFor="maintenance_fee" className="form-label">Default Maintenance Fee</label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="number"
                          className="form-control"
                          id="maintenance_fee"
                          name="maintenance_fee"
                          value={formSettings.maintenance_fee}
                          onChange={handleSettingChange}
                          min="0"
                          onBlur={() => {
                            // console.log("Maintenance fee set to:", formSettings.maintenance_fee);
                          }}
                        />
                      </div>
                      <small className="text-muted">Default monthly maintenance fee for each unit.</small>
                  </div>
                </div>
                
                <div className="row mb-3">
                  <div className="col-md-6">
                      <label htmlFor="additional_charges" className="form-label">Additional Charges</label>
                      <div className="input-group">
                        <span className="input-group-text">₹</span>
                        <input
                          type="number"
                          className="form-control"
                          id="additional_charges"
                          name="additional_charges"
                          value={formSettings.additional_charges}
                          onChange={handleSettingChange}
                          min="0"
                          onBlur={() => {
                            // console.log("Maintenance fee set to:", formSettings.maintenance_fee);
                          }}
                        />
                      </div>
                      <small className="text-muted">Additional charges fee for each unit.</small>
                  </div>
                  <div className="col-md-6">
                      <label htmlFor="maintenance_due_day" className="form-label">Maintenance Due Day</label>
                      <input
                        type="number"
                        className="form-control"
                        id="maintenance_due_day"
                        name="maintenance_due_day"
                        value={formSettings.maintenance_due_day}
                        onChange={handleSettingChange}
                        min="1"
                        max="31"
                      />
                      <small className="text-muted">Day of the month when maintenance fee is due.</small>
                  </div>
                </div>
                
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="show_owner_contact"
                    name="show_owner_contact"
                    checked={!!formSettings.show_owner_contact} // Convert to boolean
                    onChange={handleSettingChange}
                  />
                  <label className="form-check-label" htmlFor="show_owner_contact">
                    Show owner contact information to other residents
                  </label>
                </div>
              </div>
            </>
          )}
          
          {/* Appearance Tab */}
          {activeTab === 'appearance' && (
            <>
              <SectionHeader settings={settings}>Appearance Settings</SectionHeader>
              <div className="settings-form-container">
                <div className="row mb-4">
                  <div className="col-md-6">
                      <label htmlFor="theme" className="form-label">Theme</label>
                      <select
                        className="form-select"
                        id="theme"
                        name="theme"
                        value={formSettings.theme}
                        onChange={handleThemeChange}
                      >
                        {themes.map(theme => (
                          <option key={theme.value} value={theme.value}>{theme.label}</option>
                        ))}
                      </select>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_width" className="form-label">Sidebar Width (px)</label>
                      <input
                        type="number"
                        className="form-control"
                        id="sidebar_width"
                        name="sidebar_width"
                        value={formSettings.sidebar_width || '250'}
                        onChange={handleSettingChange}
                        min="180"
                        max="400"
                      />
                    </div>
                  </div>
                </div>
                
                <h5 className="mb-3">Theme Colors</h5>
                <div className="row mb-4">
                  <div className="col-md-4">
                      <label htmlFor="theme_primary_color" className="form-label">Primary Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="theme_primary_color"
                          name="theme_primary_color"
                          value={formSettings.theme_primary_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose primary color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.theme_primary_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'theme_primary_color', value: e.target.value } })}
                        />
                      </div>
                  </div>
                  <div className="col-md-4">
                      <label htmlFor="theme_secondary_color" className="form-label">Secondary Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="theme_secondary_color"
                          name="theme_secondary_color"
                          value={formSettings.theme_secondary_color || '#6c757d'}
                          onChange={handleColorChange}
                          title="Choose secondary color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.theme_secondary_color || '#6c757d'}
                          onChange={(e) => handleColorChange({ target: { name: 'theme_secondary_color', value: e.target.value } })}
                        />
                    </div>
                  </div>
                  <div className="col-md-4">
                      <label htmlFor="theme_accent_color" className="form-label">Accent Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="theme_accent_color"
                          name="theme_accent_color"
                          value={formSettings.theme_accent_color || '#ffc107'}
                          onChange={handleColorChange}
                          title="Choose accent color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.theme_accent_color || '#ffc107'}
                          onChange={(e) => handleColorChange({ target: { name: 'theme_accent_color', value: e.target.value } })}
                        />
                      </div>
                  </div>
                </div>
                
                <h5 className="mb-3">Block Titles</h5>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="block_title_color" className="form-label">Block Title Text Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="block_title_color"
                          name="block_title_color"
                          value={formSettings.block_title_color || '#8B4513'}
                          onChange={handleColorChange}
                          title="Choose block title text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.block_title_color || '#8B4513'}
                          onChange={(e) => handleColorChange({ target: { name: 'block_title_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="block_title_bg_color" className="form-label">Block Title Background Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="block_title_bg_color"
                          name="block_title_bg_color"
                          value={formSettings.block_title_bg_color || '#f8f9fa'}
                          onChange={handleColorChange}
                          title="Choose block title background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.block_title_bg_color || '#f8f9fa'}
                          onChange={(e) => handleColorChange({ target: { name: 'block_title_bg_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <h5 className="mb-3">Heading Colors</h5>
                <div className="row mb-4">
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="h1_color" className="form-label">H1 Heading Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="h1_color"
                          name="h1_color"
                          value={formSettings.h1_color || '#212529'}
                          onChange={handleColorChange}
                          title="Choose h1 heading color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.h1_color || '#212529'}
                          onChange={(e) => handleColorChange({ target: { name: 'h1_color', value: e.target.value } })}
                        />
                      </div>
                      <small className="text-muted">Used for page titles like &ldquo;Global Settings&ldquo;</small>
                    </div>
                  </div>
                  
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="h2_color" className="form-label">H2 Heading Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="h2_color"
                          name="h2_color"
                          value={formSettings.h2_color || '#212529'}
                          onChange={handleColorChange}
                          title="Choose h2 heading color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.h2_color || '#212529'}
                          onChange={(e) => handleColorChange({ target: { name: 'h2_color', value: e.target.value } })}
                        />
                      </div>
                      <small className="text-muted">Used for section headings</small>
                    </div>
                  </div>
                  
                  <div className="col-md-4">
                    <div className="mb-3">
                      <label htmlFor="h3_color" className="form-label">H3 Heading Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="h3_color"
                          name="h3_color"
                          value={formSettings.h3_color || '#212529'}
                          onChange={handleColorChange}
                          title="Choose h3 heading color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.h3_color || '#212529'}
                          onChange={(e) => handleColorChange({ target: { name: 'h3_color', value: e.target.value } })}
                        />
                      </div>
                      <small className="text-muted">Used for sub-section headings</small>
                    </div>
                  </div>
                </div>
                
                {/* Tabs Navigation */}
                <h5 className="mb-3">Tabs Navigation</h5>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="tabs_bg_color" className="form-label">Background Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="tabs_bg_color"
                          name="tabs_bg_color"
                          value={formSettings.tabs_bg_color || '#fafafa'}
                          onChange={handleColorChange}
                          title="Choose tabs background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.tabs_bg_color || '#fafafa'}
                          onChange={(e) => handleColorChange({ target: { name: 'tabs_bg_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="tabs_text_color" className="form-label">Text Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="tabs_text_color"
                          name="tabs_text_color"
                          value={formSettings.tabs_text_color || '#212529'}
                          onChange={handleColorChange}
                          title="Choose tabs text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.tabs_text_color || '#212529'}
                          onChange={(e) => handleColorChange({ target: { name: 'tabs_text_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="tabs_active_bg_color" className="form-label">Active Item Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="tabs_active_bg_color"
                          name="tabs_active_bg_color"
                          value={formSettings.tabs_active_bg_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose active item background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.tabs_active_bg_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'tabs_active_bg_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="tabs_active_text_color" className="form-label">Active Item Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="tabs_active_text_color"
                          name="tabs_active_text_color"
                          value={formSettings.tabs_active_text_color || '#ffffff'}
                          onChange={handleColorChange}
                          title="Choose active item text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.tabs_active_text_color || '#ffffff'}
                          onChange={(e) => handleColorChange({ target: { name: 'tabs_active_text_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="tabs_hover_bg_color" className="form-label">Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="tabs_hover_bg_color"
                          name="tabs_hover_bg_color"
                          value={formSettings.tabs_hover_bg_color || '#f8f9fa'}
                          onChange={handleColorChange}
                          title="Choose hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.tabs_hover_bg_color || '#f8f9fa'}
                          onChange={(e) => handleColorChange({ target: { name: 'tabs_hover_bg_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="tabs_hover_text_color" className="form-label">Hover Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="tabs_hover_text_color"
                          name="tabs_hover_text_color"
                          value={formSettings.tabs_hover_text_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose hover text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.tabs_hover_text_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'tabs_hover_text_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Sidebar Navigation section */}
                <h5 className="mb-3">Sidebar Navigation</h5>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_bg_color" className="form-label">Sidebar Background Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="sidebar_bg_color"
                          name="sidebar_bg_color"
                          value={formSettings.sidebar_bg_color || '#ffffff'}
                          onChange={handleColorChange}
                          title="Choose sidebar background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.sidebar_bg_color || '#ffffff'}
                          onChange={(e) => handleColorChange({ target: { name: 'sidebar_bg_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_text_color" className="form-label">Menu Text Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="sidebar_text_color"
                          name="sidebar_text_color"
                          value={formSettings.sidebar_text_color || '#212529'}
                          onChange={handleColorChange}
                          title="Choose sidebar text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.sidebar_text_color || '#212529'}
                          onChange={(e) => handleColorChange({ target: { name: 'sidebar_text_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_icon_color" className="form-label">Menu Icon Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="sidebar_icon_color"
                          name="sidebar_icon_color"
                          value={formSettings.sidebar_icon_color || '#212529'}
                          onChange={handleColorChange}
                          title="Choose sidebar icon color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.sidebar_icon_color || '#212529'}
                          onChange={(e) => handleColorChange({ target: { name: 'sidebar_icon_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_border_color" className="form-label">Border Color</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="sidebar_border_color"
                          name="sidebar_border_color"
                          value={formSettings.sidebar_border_color || '#dee2e6'}
                          onChange={handleColorChange}
                          title="Choose sidebar border color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.sidebar_border_color || '#dee2e6'}
                          onChange={(e) => handleColorChange({ target: { name: 'sidebar_border_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_active_bg_color" className="form-label">Active Item Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="sidebar_active_bg_color"
                          name="sidebar_active_bg_color"
                          value={formSettings.sidebar_active_bg_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose active item background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.sidebar_active_bg_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'sidebar_active_bg_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_active_text_color" className="form-label">Active Item Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="sidebar_active_text_color"
                          name="sidebar_active_text_color"
                          value={formSettings.sidebar_active_text_color || '#ffffff'}
                          onChange={handleColorChange}
                          title="Choose active item text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.sidebar_active_text_color || '#ffffff'}
                          onChange={(e) => handleColorChange({ target: { name: 'sidebar_active_text_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_hover_bg_color" className="form-label">Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="sidebar_hover_bg_color"
                          name="sidebar_hover_bg_color"
                          value={formSettings.sidebar_hover_bg_color || '#f8f9fa'}
                          onChange={handleColorChange}
                          title="Choose hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.sidebar_hover_bg_color || '#f8f9fa'}
                          onChange={(e) => handleColorChange({ target: { name: 'sidebar_hover_bg_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="sidebar_hover_text_color" className="form-label">Hover Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="sidebar_hover_text_color"
                          name="sidebar_hover_text_color"
                          value={formSettings.sidebar_hover_text_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose hover text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.sidebar_hover_text_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'sidebar_hover_text_color', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Button Color Settings section */}
                <h5 className="mb-3">Button Color Settings</h5>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_primary_bg" className="form-label">Primary Button Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_primary_bg"
                          name="btn_primary_bg"
                          value={formSettings.btn_primary_bg || formSettings.theme_primary_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose primary button background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_primary_bg || formSettings.theme_primary_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_primary_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_primary_text" className="form-label">Primary Button Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_primary_text"
                          name="btn_primary_text"
                          value={formSettings.btn_primary_text || '#ffffff'}
                          onChange={handleColorChange}
                          title="Choose primary button text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_primary_text || '#ffffff'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_primary_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_primary_hover_bg" className="form-label">Primary Button Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_primary_hover_bg"
                          name="btn_primary_hover_bg"
                          value={formSettings.btn_primary_hover_bg || '#0b5ed7'}
                          onChange={handleColorChange}
                          title="Choose primary button hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_primary_hover_bg || '#0b5ed7'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_primary_hover_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_secondary_bg" className="form-label">Secondary Button Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_secondary_bg"
                          name="btn_secondary_bg"
                          value={formSettings.btn_secondary_bg || '#6c757d'}
                          onChange={handleColorChange}
                          title="Choose secondary button background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_secondary_bg || '#6c757d'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_secondary_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_secondary_text" className="form-label">Secondary Button Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_secondary_text"
                          name="btn_secondary_text"
                          value={formSettings.btn_secondary_text || '#ffffff'}
                          onChange={handleColorChange}
                          title="Choose secondary button text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_secondary_text || '#ffffff'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_secondary_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_secondary_hover_bg" className="form-label">Secondary Button Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_secondary_hover_bg"
                          name="btn_secondary_hover_bg"
                          value={formSettings.btn_secondary_hover_bg || '#5a6268'}
                          onChange={handleColorChange}
                          title="Choose secondary button hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_secondary_hover_bg || '#5a6268'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_secondary_hover_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_success_bg" className="form-label">Success Button Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_success_bg"
                          name="btn_success_bg"
                          value={formSettings.btn_success_bg || '#198754'}
                          onChange={handleColorChange}
                          title="Choose success button background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_success_bg || '#198754'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_success_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_success_text" className="form-label">Success Button Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_success_text"
                          name="btn_success_text"
                          value={formSettings.btn_success_text || '#ffffff'}
                          onChange={handleColorChange}
                          title="Choose success button text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_success_text || '#ffffff'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_success_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_success_hover_bg" className="form-label">Success Button Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_success_hover_bg"
                          name="btn_success_hover_bg"
                          value={formSettings.btn_success_hover_bg || '#157347'}
                          onChange={handleColorChange}
                          title="Choose success button hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_success_hover_bg || '#157347'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_success_hover_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_danger_bg" className="form-label">Danger Button Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_danger_bg"
                          name="btn_danger_bg"
                          value={formSettings.btn_danger_bg || '#dc3545'}
                          onChange={handleColorChange}
                          title="Choose danger button background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_danger_bg || '#dc3545'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_danger_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_danger_text" className="form-label">Danger Button Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_danger_text"
                          name="btn_danger_text"
                          value={formSettings.btn_danger_text || '#ffffff'}
                          onChange={handleColorChange}
                          title="Choose danger button text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_danger_text || '#ffffff'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_danger_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_danger_hover_bg" className="form-label">Danger Button Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_danger_hover_bg"
                          name="btn_danger_hover_bg"
                          value={formSettings.btn_danger_hover_bg || '#bb2d3b'}
                          onChange={handleColorChange}
                          title="Choose danger button hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_danger_hover_bg || '#bb2d3b'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_danger_hover_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_warning_bg" className="form-label">Warning Button Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_warning_bg"
                          name="btn_warning_bg"
                          value={formSettings.btn_warning_bg || '#ffc107'}
                          onChange={handleColorChange}
                          title="Choose warning button background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_warning_bg || '#ffc107'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_warning_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_warning_text" className="form-label">Warning Button Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_warning_text"
                          name="btn_warning_text"
                          value={formSettings.btn_warning_text || '#000000'}
                          onChange={handleColorChange}
                          title="Choose warning button text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_warning_text || '#000000'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_warning_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_warning_hover_bg" className="form-label">Warning Button Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_warning_hover_bg"
                          name="btn_warning_hover_bg"
                          value={formSettings.btn_warning_hover_bg || '#ffca2c'}
                          onChange={handleColorChange}
                          title="Choose warning button hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_warning_hover_bg || '#ffca2c'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_warning_hover_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_info_bg" className="form-label">Info Button Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_info_bg"
                          name="btn_info_bg"
                          value={formSettings.btn_info_bg || '#0dcaf0'}
                          onChange={handleColorChange}
                          title="Choose info button background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_info_bg || '#0dcaf0'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_info_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_info_text" className="form-label">Info Button Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_info_text"
                          name="btn_info_text"
                          value={formSettings.btn_info_text || '#000000'}
                          onChange={handleColorChange}
                          title="Choose info button text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_info_text || '#000000'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_info_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_info_hover_bg" className="form-label">Info Button Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_info_hover_bg"
                          name="btn_info_hover_bg"
                          value={formSettings.btn_info_hover_bg || '#31d2f2'}
                          onChange={handleColorChange}
                          title="Choose info button hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_info_hover_bg || '#31d2f2'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_info_hover_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_outline_border" className="form-label">Outline Button Border</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_outline_border"
                          name="btn_outline_border"
                          value={formSettings.btn_outline_border || formSettings.theme_primary_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose outline button border color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_outline_border || formSettings.theme_primary_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_outline_border', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_outline_text" className="form-label">Outline Button Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_outline_text"
                          name="btn_outline_text"
                          value={formSettings.btn_outline_text || formSettings.theme_primary_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose outline button text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_outline_text || formSettings.theme_primary_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_outline_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_outline_hover_bg" className="form-label">Outline Button Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_outline_hover_bg"
                          name="btn_outline_hover_bg"
                          value={formSettings.btn_outline_hover_bg || formSettings.theme_primary_color || '#0d6efd'}
                          onChange={handleColorChange}
                          title="Choose outline button hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_outline_hover_bg || formSettings.theme_primary_color || '#0d6efd'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_outline_hover_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_outline_hover_text" className="form-label">Outline Button Hover Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_outline_hover_text"
                          name="btn_outline_hover_text"
                          value={formSettings.btn_outline_hover_text || '#ffffff'}
                          onChange={handleColorChange}
                          title="Choose outline button hover text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_outline_hover_text || '#ffffff'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_outline_hover_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_disabled_bg" className="form-label">Disabled Button Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_disabled_bg"
                          name="btn_disabled_bg"
                          value={formSettings.btn_disabled_bg || '#e9ecef'}
                          onChange={handleColorChange}
                          title="Choose disabled button background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_disabled_bg || '#e9ecef'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_disabled_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_disabled_text" className="form-label">Disabled Button Text</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_disabled_text"
                          name="btn_disabled_text"
                          value={formSettings.btn_disabled_text || '#6c757d'}
                          onChange={handleColorChange}
                          title="Choose disabled button text color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_disabled_text || '#6c757d'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_disabled_text', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_disabled_border" className="form-label">Disabled Button Border</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_disabled_border"
                          name="btn_disabled_border"
                          value={formSettings.btn_disabled_border || '#e9ecef'}
                          onChange={handleColorChange}
                          title="Choose disabled button border color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_disabled_border || '#e9ecef'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_disabled_border', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="btn_disabled_hover_bg" className="form-label">Disabled Button Hover Background</label>
                      <div className="input-group">
                        <input
                          type="color"
                          className="form-control form-control-color"
                          id="btn_disabled_hover_bg"
                          name="btn_disabled_hover_bg"
                          value={formSettings.btn_disabled_hover_bg || '#e9ecef'}
                          onChange={handleColorChange}
                          title="Choose disabled button hover background color"
                        />
                        <input
                          type="text"
                          className="form-control"
                          value={formSettings.btn_disabled_hover_bg || '#e9ecef'}
                          onChange={(e) => handleColorChange({ target: { name: 'btn_disabled_hover_bg', value: e.target.value } })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Button Preview */}
                <div className="preview-card mb-4">
                  <h5>Button Preview</h5>
                  
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <button className="btn btn-primary" style={{ 
                      backgroundColor: formSettings.btn_primary_bg || formSettings.theme_primary_color || '#0d6efd', 
                      borderColor: formSettings.btn_primary_bg || formSettings.theme_primary_color || '#0d6efd',
                      color: formSettings.btn_primary_text || '#ffffff' 
                    }} key={`primary-${formSettings.btn_primary_bg || 'default'}-${formSettings.btn_primary_text || 'default'}`}>
                      Primary
                    </button>
                    <button className="btn btn-secondary" style={{ 
                      backgroundColor: formSettings.btn_secondary_bg || '#6c757d', 
                      borderColor: formSettings.btn_secondary_bg || '#6c757d',
                      color: formSettings.btn_secondary_text || '#ffffff' 
                    }} key={`secondary-${formSettings.btn_secondary_bg || 'default'}-${formSettings.btn_secondary_text || 'default'}`}>
                      Secondary
                    </button>
                    <button className="btn btn-success" style={{ 
                      backgroundColor: formSettings.btn_success_bg || '#198754', 
                      borderColor: formSettings.btn_success_bg || '#198754',
                      color: formSettings.btn_success_text || '#ffffff' 
                    }} key={`success-${formSettings.btn_success_bg || 'default'}-${formSettings.btn_success_text || 'default'}`}>
                      Success
                    </button>
                    <button className="btn btn-danger" style={{ 
                      backgroundColor: formSettings.btn_danger_bg || '#dc3545', 
                      borderColor: formSettings.btn_danger_bg || '#dc3545',
                      color: formSettings.btn_danger_text || '#ffffff' 
                    }} key={`danger-${formSettings.btn_danger_bg || 'default'}-${formSettings.btn_danger_text || 'default'}`}>
                      Danger
                    </button>
                    <button className="btn btn-warning" style={{ 
                      backgroundColor: formSettings.btn_warning_bg || '#ffc107', 
                      borderColor: formSettings.btn_warning_bg || '#ffc107',
                      color: formSettings.btn_warning_text || '#000000' 
                    }} key={`warning-${formSettings.btn_warning_bg || 'default'}-${formSettings.btn_warning_text || 'default'}`}>
                      Warning
                    </button>
                    <button className="btn btn-info" style={{ 
                      backgroundColor: formSettings.btn_info_bg || '#0dcaf0', 
                      borderColor: formSettings.btn_info_bg || '#0dcaf0',
                      color: formSettings.btn_info_text || '#000000' 
                    }} key={`info-${formSettings.btn_info_bg || 'default'}-${formSettings.btn_info_text || 'default'}`}>
                      Info
                    </button>
                    <button className="btn btn-outline-primary" style={{ 
                      backgroundColor: 'transparent',
                      borderColor: formSettings.btn_outline_border || formSettings.theme_primary_color || '#0d6efd',
                      color: formSettings.btn_outline_text || formSettings.theme_primary_color || '#0d6efd'
                    }} key={`outline-${formSettings.btn_outline_border || 'default'}-${formSettings.btn_outline_text || 'default'}`}>
                      Outline
                    </button>
                    <button className="btn" disabled style={{ 
                      backgroundColor: formSettings.btn_disabled_bg || '#e9ecef', 
                      borderColor: formSettings.btn_disabled_border || '#e9ecef',
                      color: formSettings.btn_disabled_text || '#6c757d',
                      cursor: 'not-allowed',
                      opacity: 0.65
                    }} key={`disabled-${formSettings.btn_disabled_bg || 'default'}-${formSettings.btn_disabled_text || 'default'}`}>
                      Disabled
                    </button>
                  </div>
                  
                  {/* Hover Preview Information */}
                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      <strong>Hover effects:</strong> Primary → {formSettings.btn_primary_hover_bg || '#0b5ed7'}, 
                      Secondary → {formSettings.btn_secondary_hover_bg || '#5a6268'}, 
                      Success → {formSettings.btn_success_hover_bg || '#157347'},
                      Danger → {formSettings.btn_danger_hover_bg || '#bb2d3b'},
                      Warning → {formSettings.btn_warning_hover_bg || '#ffca2c'},
                      Info → {formSettings.btn_info_hover_bg || '#31d2f2'}.
                      Outline button on hover: Background becomes {formSettings.btn_outline_hover_bg || formSettings.theme_primary_color || '#0d6efd'} with text {formSettings.btn_outline_hover_text || '#ffffff'}
                    </small>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* Regional Settings Tab */}
          {activeTab === 'regional' && (
            <>
              <SectionHeader settings={settings}>Regional Settings</SectionHeader>
              <div className="settings-form-container">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="currency" className="form-label">Currency</label>
                      <select
                        className="form-select"
                        id="currency"
                        name="currency"
                        value={formSettings.currency}
                        onChange={handleCurrencyChange}
                      >
                        {currencies.map(curr => (
                          <option key={curr.code} value={curr.code}>
                            {curr.code} - {curr.name} ({curr.symbol})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="dateFormat" className="form-label">Date Format</label>
                      <select
                        className="form-select"
                        id="dateFormat"
                        name="dateFormat"
                        value={formSettings.dateFormat}
                        onChange={handleDateFormatChange}
                      >
                        {dateFormats.map(format => (
                          <option key={format.value} value={format.value}>
                            {format.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="timezone" className="form-label">Time Zone</label>
                      <select
                        className="form-select"
                        id="timezone"
                        name="timezone"
                        value={formSettings.timezone}
                        onChange={handleSettingChange}
                      >
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="America/New_York">America/New_York (EST/EDT)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                        <option value="Europe/London">Europe/London (GMT/BST)</option>
                        <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                        <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                        <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                      </select>
                      <small className="text-muted">Used for scheduling and showing times in the app.</small>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label htmlFor="language" className="form-label">Language</label>
                      <select
                        className="form-select"
                        id="language"
                        name="language"
                        value={formSettings.language}
                        onChange={handleSettingChange}
                        disabled
                      >
                        <option value="en">English</option>
                        <option value="hi">Hindi</option>
                        <option value="es">Spanish</option>
                      </select>
                      <small className="text-muted">More languages coming soon.</small>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          
          {/* System Information Tab */}
          {activeTab === 'system' && (
            <>
              <SectionHeader settings={settings}>System Information</SectionHeader>
              <div className="settings-form-container">
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">App Version</label>
                      <input
                        type="text"
                        className="form-control"
                        value="1.0.0"
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Database Version</label>
                      <input
                        type="text"
                        className="form-control"
                        value="1.0.0"
                        disabled
                      />
                    </div>
                  </div>
                </div>
                
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Last Database Backup</label>
                      <input
                        type="text"
                        className="form-control"
                        value="Never"
                        disabled
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Server Status</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          value="Online"
                          disabled
                        />
                        <span className="input-group-text bg-success text-white">
                          <i className="fas fa-check-circle"></i>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="alert alert-info mb-0">
                  <h5><i className="fas fa-info-circle me-2"></i>System Information</h5>
                  <p className="mb-0">This section provides system details for troubleshooting. For support, please contact your system administrator.</p>
                </div>
              </div>
            </>
          )}
          
          {/* Advanced Settings Tab */}
          {activeTab === 'advanced' && (
            <>
              <SectionHeader settings={settings}>Advanced Settings</SectionHeader>
              <div className="settings-form-container">
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="alert alert-primary mb-4">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-info-circle fa-2x me-3"></i>
                        <div>
                          <h5 className="mb-1">Theme Changes Apply Globally</h5>
                          <p className="mb-0">All theme settings, colors, and custom CSS defined here will automatically apply to <strong>all user interfaces</strong> (Admin, Guard, and Owner dashboards). Other users cannot modify these settings.</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor="custom_css" className="form-label">Custom CSS Override</label>
                      <div className="alert alert-warning">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        <strong>Warning:</strong> Adding custom CSS may affect the application&apos;s layout and functionality. Use with caution.
                      </div>
                      <textarea
                        className="form-control monospace-font"
                        id="custom_css"
                        name="custom_css"
                        value={formSettings.custom_css || ''}
                        onChange={handleSettingChange}
                        placeholder="/* Add your custom CSS here */"
                        rows={15}
                        style={{ fontFamily: 'monospace', fontSize: '14px' }}
                      ></textarea>
                      <small className="text-muted">
                        Add custom CSS rules to override the default styles of the application. 
                        This CSS will be applied last and will override all other styles.
                      </small>
                    </div>
                    
                    <div className="mb-3">
                      <button
                        className="btn btn-sm btn-outline-secondary me-2"
                        onClick={() => {
                          // Apply CSS without saving
                          const styleEl = document.getElementById('app-custom-css-override') || document.createElement('style');
                          styleEl.id = 'app-custom-css-override';
                          styleEl.textContent = formSettings.custom_css || '';
                          if (!styleEl.parentNode) {
                            document.head.appendChild(styleEl);
                          }
                          toast.info('Custom CSS applied temporarily. Save settings to make it permanent.');
                        }}
                      >
                        <i className="fas fa-eye me-2"></i>
                        Preview CSS
                      </button>
                      
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to clear all custom CSS?')) {
                            setFormSettings({
                              ...formSettings,
                              custom_css: ''
                            });
                            
                            // Remove custom CSS
                            const styleEl = document.getElementById('app-custom-css-override');
                            if (styleEl) {
                              styleEl.textContent = '';
                            }
                            toast.success('Custom CSS cleared.');
                          }
                        }}
                      >
                        <i className="fas fa-trash me-2"></i>
                        Clear CSS
                      </button>
                    </div>
                    
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle me-2"></i>
                      <strong>Tip:</strong> Use browser dev tools to identify class names and element selectors before writing custom CSS.
                    </div>
                    
                    <div className="alert alert-primary">
                      <h6><i className="fas fa-lightbulb me-2"></i>Example CSS Snippets:</h6>
                      <pre className="mt-2 bg-light p-2 rounded" style={{ fontSize: '12px' }}>
                        {`.card { 
                          border-radius: 15px; 
                          box-shadow: 0 4px 8px rgba(0,0,0,0.1); 
                        }

                        .btn-primary { 
                          background: linear-gradient(45deg, #0d6efd, #198754); 
                          border: none;
                        }`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Financial Settings Tab */}
          {activeTab === 'financial' && (
            <>
              <SectionHeader settings={settings}>Financial Settings</SectionHeader>
              <div className="settings-form-container">
                <div className="row mb-4">
                  <div className="col-md-12">
                    <label htmlFor="financial_year" className="form-label">Financial Year Start (Year)</label>
                    <input
                      type="number"
                      className="form-control"
                      id="financial_year"
                      name="financial_year"
                      value={formSettings.financial_year}
                      onChange={handleSettingChange}
                      min="1"
                    />
                    <small className="form-text text-muted">
                      Enter the year when your financial year begins.
                    </small>
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label htmlFor="scanner_qr_url" className="form-label">Scanner QR Upload</label>
                    <input
                      type="file"
                      className="form-control"
                      id="scanner_qr_url"
                      name="scanner_qr_url"
                      accept="image/*"
                      onChange={handleScannerChange}
                    />
                    <small className="text-muted">Recommended size: 200x200 pixels. Max file size: 5MB.</small>
                    <div className='col-md-6'>
                    {(scannerPreview || memoizedScannerUrl) && (
                      <div className='row align-items-start gap-1'>
                        <div className='col-md-5'>
                          <img 
                            src={scannerPreview || memoizedScannerUrl}
                            alt="Society Scanner Preview"
                            className="mt-2 img-thumbnail"
                            style={{ maxWidth: '100px', maxHeight: '100px' }}
                          />
                        </div>
                        <div className='col-md-2'>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm mt-2"
                            onClick={handleRemoveScanner}
                            title="Remove current logo"
                          >
                            <i className="fas fa-trash logoRemoveTrash"></i>
                          </button>
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="scanner_owner_name" className="form-label">Scanner Owner Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="scanner_owner_name"
                      name="scanner_owner_name"
                      value={formSettings.scanner_owner_name}
                      onChange={handleSettingChange}
                    />
                  </div>
                </div>
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label htmlFor="scanner_upi_id" className="form-label">Scanner UPI ID</label>
                    <input
                      type="text"
                      className="form-control"
                      id="scanner_upi_id"
                      name="scanner_upi_id"
                      value={formSettings.scanner_upi_id}
                      onChange={handleSettingChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label htmlFor="scanner_upi_number" className="form-label">Scanner UPI Number</label>
                    <input
                      type="number"
                      className="form-control"
                      id="scanner_upi_number"
                      name="scanner_upi_number"
                      value={formSettings.scanner_upi_number}
                      onChange={handleSettingChange}
                    />
                  </div>
                </div>
                <div className="col-md-12 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="show_society_transactions"
                    name="show_society_transactions"
                    checked={!!formSettings.show_society_transactions}
                    onChange={handleSettingChange}
                  />
                  <label className="form-check-label" htmlFor="show_society_transactions">
                    Show Society Financial Data to Residents
                  </label>
                  <small className="form-text text-muted d-block">
                    When enabled, the society financial data will be shown to the residents.
                  </small>
                </div>
              </div>
            </>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default Settings; 